import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  LayoutGrid,
  ArrowLeft,
  ArrowRight,
  Plus,
  Clock,
  Tag,
  AlertCircle,
  X,
  Edit2,
  Trash2,
  Check,
  Sparkles,
  Wand2,
  Info,
  Trash
} from 'lucide-react';
import {
  startOfMonth,
  endOfMonth,
  startOfDay,
  addDays,
  subDays,
  eachDayOfInterval,
  format,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  isToday,
  parseISO,
  addHours
} from 'date-fns';
import { supabase } from '../lib/supabase';
import DayViewModal from '../components/DayViewModal';
import AutoFillModal from '../components/calendar/AutoFillModal';
import ScheduleInfoButton from '../components/calendar/ScheduleInfoButton';
import ClearCalendarModal from '../components/calendar/ClearCalendarModal';
import type { Task } from '../types/database';
import { useTasks } from '../lib/context/TaskContext';

interface CalendarEvent {
  id: string;
  title: string;
  start_date: string;
  end_date?: string;
  color?: string;
  tags: string[];
}

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showDayModal, setShowDayModal] = useState(false);
  const [view, setView] = useState<'month' | 'week' | 'day'>('month');
  const [isAdding, setIsAdding] = useState(false);
  const [dragOverDay, setDragOverDay] = useState<Date | null>(null);
  const [showClearModal, setShowClearModal] = useState(false);
  const [showAutoFillModal, setShowAutoFillModal] = useState(false);
  const { tasks } = useTasks();

  useEffect(() => {
    loadEvents();
  }, [currentDate]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const startDate = startOfMonth(currentDate);
      const endDate = endOfMonth(currentDate);

      const { data, error: fetchError } = await supabase
        .from('calendar_events')
        .select('*')
        .gte('start_date', startDate.toISOString())
        .lte('start_date', endDate.toISOString())
        .order('start_date', { ascending: true });

      if (fetchError) throw fetchError;
      setEvents(data || []);

    } catch (err) {
      console.error('Error loading events:', err);
      setError(err instanceof Error ? err.message : 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent, day: Date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDay(day);
  };

  const handleDragLeave = () => {
    setDragOverDay(null);
  };

  const handleDrop = async (e: React.DragEvent, day: Date) => {
    e.preventDefault();
    setDragOverDay(null);
    e.dataTransfer.dropEffect = 'move';
    
    try {
      const taskData = e.dataTransfer.getData('application/json');
      if (!taskData) return;
      
      const task: Task = JSON.parse(taskData);
      
      // Create a calendar event from the task
      const eventDate = new Date(day);
      // Set a default time (e.g., 9 AM)
      eventDate.setHours(9, 0, 0, 0);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Create the calendar event
      const { data: newEvent, error } = await supabase
        .from('calendar_events')
        .insert({
          user_id: user.id,
          title: task.title,
          start_date: eventDate.toISOString(),
          tags: ['task'],
          color: task.color || '#4f46e5'
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Add the new event to the local state
      setEvents(prev => [...prev, newEvent]);
      
      // Optionally, mark the task as completed
      // await toggleTask(task.id, true);
      
      // Show visual feedback
      toast.success('Task added to calendar!');
    } catch (err) {
      console.error('Error creating event from task:', err);
      setError(err instanceof Error ? err.message : 'Failed to create event from task');
    }
  };

  const handleAddEvent = () => {
    setIsAdding(true);
    setShowDayModal(true);
  };

  const handleToggleEventComplete = (eventId: string) => {
    // TODO: Implement event completion toggle
    console.log('Toggling event:', eventId);
  };

  const handleBackToMonth = () => {
    setView('month');
    setSelectedDate(null);
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  // Get the first day of the month
  const firstDayOfMonth = startOfDay(monthStart);
  // Get the start of the first week (previous Sunday if month doesn't start on Sunday)
  const calendarStart = subDays(firstDayOfMonth, firstDayOfMonth.getDay());
  // Get the end of the last week (next Saturday if month doesn't end on Saturday)
  const calendarEnd = addDays(monthEnd, 6 - monthEnd.getDay());
  // Get all days that should be shown in the calendar
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Generate time slots for the day view
  const timeSlots = selectedDate ? Array.from({ length: 24 }, (_, i) => {
    const date = new Date(selectedDate);
    date.setHours(i, 0, 0, 0);
    return date;
  }) : [];

  const sortedDayEvents = selectedDate
    ? events
        .filter(event => 
          selectedDate && isSameDay(parseISO(event.start_date), selectedDate)
        )
        .sort((a, b) => parseISO(a.start_date).getTime() - parseISO(b.start_date).getTime())
    : [];

  // Get week start and end dates
  const weekStart = selectedDate ? startOfDay(selectedDate) : startOfDay(currentDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8 overflow-x-hidden w-full"
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Calendar</h1>
            <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-md">
              Manage your content schedule and events
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAutoFillModal(true)}
              className="flex items-center space-x-1 px-3 py-1.5 text-xs text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
            >
              <Sparkles className="h-3.5 w-3.5" />
              <span>AI Posting Schedule</span>
            </motion.button>
            <ScheduleInfoButton />
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowClearModal(true)}
              className="flex items-center space-x-1 px-3 py-1.5 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
            >
              <Trash className="h-3.5 w-3.5" />
              <span>Clear Calendar</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleAddEvent}
              className="flex items-center space-x-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
            >
              <Plus className="h-5 w-5" />
              <span>Add Event</span>
            </motion.button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 sm:p-4 flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 dark:text-red-400" />
            <p className="text-xs sm:text-sm text-red-600 dark:text-red-300">{error}</p>
          </div>
        )}

        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm w-full">
          {/* Calendar Header */}
          <div className="p-3 sm:p-6 border-b border-gray-200 dark:border-dark-700">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              {view === 'day' && (
                <button
                  onClick={handleBackToMonth}
                  className="flex items-center space-x-1 sm:space-x-2 text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 text-sm"
                >
                  <ArrowLeft className="h-5 w-5" />
                  <span>Back to Month View</span>
                </button>
              )}
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0">
              <div className="flex items-center space-x-2 sm:space-x-4">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => view === 'month' ? setCurrentDate(subMonths(currentDate, 1)) : setSelectedDate(subDays(selectedDate || new Date(), 1))}
                  className="p-1.5 sm:p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-full transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                </motion.button>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                  {view === 'month' 
                    ? format(currentDate, 'MMMM yyyy')
                    : format(selectedDate || new Date(), 'MMMM d, yyyy')}
                </h2>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => view === 'month' ? setCurrentDate(addMonths(currentDate, 1)) : setSelectedDate(addDays(selectedDate || new Date(), 1))}
                  className="p-1.5 sm:p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-full transition-colors"
                >
                  <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                </motion.button>
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <div className="flex bg-gray-100 dark:bg-dark-700 p-0.5 sm:p-1 rounded-lg">
                  <button
                    onClick={() => setView('month')}
                    className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded-md transition-colors ${
                      view === 'month'
                        ? 'bg-white dark:bg-dark-800 text-indigo-600 dark:text-indigo-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    Month
                  </button>
                  <button
                    onClick={() => setView('week')}
                    className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded-md transition-colors ${
                      view === 'week'
                        ? 'bg-white dark:bg-dark-800 text-indigo-600 dark:text-indigo-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    Week
                  </button>
                  <button
                    onClick={() => setView('day')}
                    className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded-md transition-colors ${
                      view === 'day'
                        ? 'bg-white dark:bg-dark-800 text-indigo-600 dark:text-indigo-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    Day
                  </button>
                </div>
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
                >
                  Today
                </button>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          {view === 'month' && (
            <div className="p-3 sm:p-6 overflow-x-auto">
            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div
                  key={day}
                  className="text-center text-sm font-medium text-gray-500 dark:text-gray-400"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 divide-x divide-y divide-gray-200 dark:divide-dark-700 border border-gray-200 dark:border-dark-700 min-w-[700px] max-w-full">
              {calendarDays.map(day => (
                <div
                  onDragOver={(e) => handleDragOver(e, day)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, day)}
                  key={day.toISOString()}
                  className={`relative h-32 p-1 transition-colors ${
                    !isSameMonth(day, currentDate) ? 'bg-gray-50 dark:bg-dark-800/50' : 'bg-white dark:bg-dark-800'
                  } ${
                    dragOverDay && isSameDay(day, dragOverDay) ? 'bg-indigo-50 dark:bg-indigo-900/20 border-2 border-indigo-300 dark:border-indigo-700' : ''
                  } cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors`}
                  onClick={() => {
                    setSelectedDate(day);
                    setView('day');
                  }}
                >
                  <span className={`calendar-day-number ${
                    !isSameMonth(day, currentDate)
                      ? 'text-gray-400 dark:text-gray-600'
                      : isToday(day)
                      ? 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
                      : 'text-gray-900 dark:text-white'
                  }`}>
                    {format(day, 'd')}
                  </span>
                  <div className="calendar-day-content">
                    {events
                      .filter(event => isSameDay(parseISO(event.start_date), day))
                      .map(event => (
                        <div
                          key={event.id}
                          className={`mb-1 p-1 rounded text-xs truncate ${
                            event.color
                              ? `bg-${event.color}-100 dark:bg-${event.color}-900/20 text-${event.color}-600 dark:text-${event.color}-400`
                              : 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
                          }`}
                        >
                          {event.title}
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
            </div>
          )}

          {view === 'week' && (
            <div className="p-3 sm:p-6 overflow-x-auto">
              {/* Week Headers */}
              <div className="grid grid-cols-7 divide-x divide-gray-200 dark:divide-dark-700 border border-gray-200 dark:border-dark-700">
                {weekDays.map(day => (
                  <div
                    key={day.toISOString()}
                    className="p-2 text-center"
                  >
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {format(day, 'EEE')}
                    </div>
                    <div className={`text-lg font-semibold mt-1 ${
                      isToday(day)
                        ? 'text-indigo-600 dark:text-indigo-400'
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {format(day, 'd')}
                    </div>
                  </div>
                ))}
              </div>

              {/* Time Slots */}
              <div className="grid grid-cols-7 divide-x divide-gray-200 dark:divide-dark-700 border-x border-b border-gray-200 dark:border-dark-700 min-w-[700px] max-w-full">
                {weekDays.map(day => (
                  <div key={day.toISOString()} className="min-h-[800px] relative">
                    {events
                      .filter(event => isSameDay(parseISO(event.start_date), day))
                      .map(event => (
                        <div
                          key={event.id}
                          className={`absolute w-full p-2 rounded-lg m-1 ${
                            event.color
                              ? `bg-${event.color}-100 dark:bg-${event.color}-900/20 text-${event.color}-600 dark:text-${event.color}-400`
                              : 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
                          }`}
                          style={{
                            top: `${(parseISO(event.start_date).getHours() * 100) / 24}%`,
                            height: '50px'
                          }}
                        >
                          <div className="text-xs font-medium truncate">{event.title}</div>
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {view === 'day' && (
            <div className="p-6 space-y-4">
              {/* Time slots */}
              {timeSlots.map((timeSlot, index) => {
                const slotEvents = events.filter(event => {
                  const eventTime = parseISO(event.start_date);
                  return eventTime.getHours() === timeSlot.getHours();
                });
                
                return (
                  <div key={index} className="time-slot group">
                    <div className="flex items-center mb-2">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-16">
                        {format(timeSlot, 'h:mm a')}
                      </span>
                      <div className="flex-1 ml-4">
                        {slotEvents.length > 0 ? (
                          <div className="space-y-2">
                            {slotEvents.map(event => (
                              <div
                                key={event.id}
                                className="bg-white dark:bg-dark-800 rounded-lg p-3 border border-gray-100 dark:border-dark-700 hover:shadow-md transition-all duration-200"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                      {event.title}
                                    </h4>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => handleToggleEventComplete(event.id)}
                                      className="p-1 text-gray-400 hover:text-green-500 rounded-full hover:bg-gray-100 dark:hover:bg-dark-700"
                                    >
                                      <Check className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => {/* TODO: Implement edit */}}
                                      className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-full hover:bg-gray-100 dark:hover:bg-dark-700"
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => {/* TODO: Implement delete */}}
                                      className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-full hover:bg-gray-100 dark:hover:bg-dark-700"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                                {event.tags && event.tags.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {event.tags.map(tag => (
                                      <span
                                        key={tag}
                                        className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 rounded-full"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <button
                            onClick={handleAddEvent}
                            className="w-full p-2 border border-dashed border-gray-200 dark:border-dark-700 rounded-lg text-gray-400 dark:text-gray-500 hover:border-indigo-500 dark:hover:border-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <Plus className="h-4 w-4 mx-auto" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Day View Modal */}
        <DayViewModal
          isOpen={showDayModal}
          onClose={() => setShowDayModal(false)}
          date={selectedDate || new Date()}
          events={events.filter(event => 
            selectedDate && isSameDay(parseISO(event.start_date), selectedDate)
          )}
          onAddEvent={handleAddEvent}
          onToggleEventComplete={handleToggleEventComplete}
        />
      </motion.div>

      {/* Clear Calendar Modal */}
      <ClearCalendarModal
        isOpen={showClearModal}
        onClose={() => setShowClearModal(false)}
        onSuccess={loadEvents}
      />
      
      {/* AI Content Planner Modal */}
      <AutoFillModal
        isOpen={showAutoFillModal}
        onClose={() => setShowAutoFillModal(false)}
        onSuccess={loadEvents}
        currentMonth={currentDate}
      />
    </>
  );
};

export default Calendar;